import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ScrollText, Wallet, CalendarDays, Save, ListChecks, AlertTriangle, Clock, FileText, Timer, Users, Banknote, FolderArchive } from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const API = `${backendUrl}/api`;

const RuleSection = ({ icon: Icon, title, accent, testId, children }) => (
  <Card className="overflow-hidden" data-testid={testId}>
    <CardHeader className={`flex flex-row items-center gap-3 ${accent}`}>
      <div className="p-2.5 rounded-xl bg-white/20">
        <Icon className="h-6 w-6 text-white" />
      </div>
      <CardTitle className="text-xl text-white">{title}</CardTitle>
    </CardHeader>
    <CardContent className="pt-6">{children}</CardContent>
  </Card>
);

const WARNING_RULE_CATEGORIES = [
  { key: 'izin', label: 'İzin Uyarıları', icon: CalendarDays, desc: 'İzin ile ilgili uyarı kuralları' },
  { key: 'mesai', label: 'Mesai Uyarıları', icon: Clock, desc: 'Mesai/devam ile ilgili uyarı kuralları' },
  { key: 'belge', label: 'Belge Uyarıları', icon: FileText, desc: 'Belge ile ilgili uyarı kuralları' },
  { key: 'fazla_mesai', label: 'Fazla Mesai Uyarıları', icon: Timer, desc: 'Fazla mesai ile ilgili uyarı kuralları' },
  { key: 'maas', label: 'Maaş Uyarıları', icon: Wallet, desc: 'Maaş ile ilgili uyarı kuralları' },
  { key: 'avans', label: 'Avans Uyarıları', icon: Banknote, desc: 'Avans ile ilgili uyarı kuralları' },
];

const AVANS_FIELDS = [
  { key: 'max_avans_yuzdesi', label: 'Maksimum avans maaş yüzdesi', suffix: '%', placeholder: 'örn. 50' },
  { key: 'max_taksit_sayisi', label: 'Maksimum taksit sayısı', suffix: 'taksit', placeholder: 'örn. 4' },
  { key: 'yillik_avans_adedi', label: 'Yılda verilecek avans adedi', suffix: 'adet', placeholder: 'örn. 2' },
  { key: 'ilk_avans_gun', label: 'İlk avans, işe giriş tarihinden kaç gün sonra aktif olacak', suffix: 'gün', placeholder: 'örn. 90' },
];

const HRRules = () => {
  const [avans, setAvans] = useState({
    max_avans_yuzdesi: '',
    max_taksit_sayisi: '',
    yillik_avans_adedi: '',
    ilk_avans_gun: '',
    taksit_varken_avans: 'hayir',
  });
  const [savingAvans, setSavingAvans] = useState(false);

  // ===== İzin Uyarı Kuralları =====
  const emptyWarnForm = { title: '', leave_types: [], firms: [], period_type: 'month3', custom_days: '', threshold_days: '' };
  const [izinWarnOpen, setIzinWarnOpen] = useState(false);
  const [izinRules, setIzinRules] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [wForm, setWForm] = useState(emptyWarnForm);
  const [savingWarn, setSavingWarn] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);

  const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('fortis_token')}` } });

  const fetchIzinRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/warning-rules?rule_type=izin`, authH());
      setIzinRules(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchSubcontractors = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/subcontractors`, authH());
      setSubcontractors(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const openIzinWarnModal = () => {
    setWForm(emptyWarnForm);
    setEditingRuleId(null);
    fetchIzinRules();
    fetchSubcontractors();
    setIzinWarnOpen(true);
  };

  const startEditRule = (r) => {
    setEditingRuleId(r.id);
    setWForm({
      title: r.title || '',
      leave_types: r.leave_types || [],
      firms: r.firms || [],
      period_type: r.period_type || 'month3',
      custom_days: r.custom_days ? String(r.custom_days) : '',
      threshold_days: r.threshold_days ? String(r.threshold_days) : '',
    });
  };

  const cancelEdit = () => {
    setEditingRuleId(null);
    setWForm(emptyWarnForm);
  };

  const toggleArr = (field, val) => {
    setWForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const saveIzinRule = async () => {
    if (!wForm.title.trim()) { toast.error('Başlık zorunludur'); return; }
    if (!wForm.leave_types.length) { toast.error('En az bir izin türü seçin'); return; }
    if (!wForm.threshold_days || Number(wForm.threshold_days) <= 0) { toast.error('İzin gün eşiği girin'); return; }
    if (wForm.period_type === 'custom' && (!wForm.custom_days || Number(wForm.custom_days) <= 0)) { toast.error('Gün sayısı girin'); return; }
    setSavingWarn(true);
    try {
      const body = {
        rule_type: 'izin',
        title: wForm.title.trim(),
        leave_types: wForm.leave_types,
        firms: wForm.firms,
        period_type: wForm.period_type,
        custom_days: wForm.period_type === 'custom' ? Number(wForm.custom_days) : 0,
        threshold_days: Number(wForm.threshold_days),
      };
      if (editingRuleId) {
        await axios.put(`${API}/hr/warning-rules/${editingRuleId}`, body, authH());
        toast.success('Uyarı kuralı güncellendi');
      } else {
        await axios.post(`${API}/hr/warning-rules`, body, authH());
        toast.success('Uyarı kuralı eklendi');
      }
      setWForm(emptyWarnForm);
      setEditingRuleId(null);
      fetchIzinRules();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kural kaydedilemedi');
    } finally { setSavingWarn(false); }
  };

  const deleteIzinRule = async (id) => {
    try {
      await axios.delete(`${API}/hr/warning-rules/${id}`, authH());
      toast.success('Kural silindi');
      fetchIzinRules();
    } catch (e) { toast.error('Silinemedi'); }
  };

  const firmOptions = [{ id: 'fortis', name: 'FORTİS' }, ...subcontractors.map((s) => ({ id: s.id, name: s.name }))];
  const PERIOD_OPTS = [
    { key: 'month1', label: 'Son 1 Ay' },
    { key: 'month3', label: 'Son 3 Ay' },
    { key: 'year1', label: 'Son 1 Yıl' },
    { key: 'custom', label: 'Elle (gün)' },
  ];
  const LEAVE_OPTS = [
    { key: 'paid_leave', label: 'Ücretli İzin' },
    { key: 'unpaid_leave', label: 'Ücretsiz İzin' },
  ];

  // ===== Fazla Mesai Uyarı Kuralları =====
  const emptyFmForm = { title: '', overtime_types: [], firms: [], period_type: 'month3', custom_days: '', threshold_days: '' };
  const [fmWarnOpen, setFmWarnOpen] = useState(false);
  const [fmRules, setFmRules] = useState([]);
  const [fmForm, setFmForm] = useState(emptyFmForm);
  const [savingFm, setSavingFm] = useState(false);
  const [editingFmId, setEditingFmId] = useState(null);
  const OT_OPTS = [
    { key: 'weekday', label: 'Gün İçi (Normal)' },
    { key: 'holiday', label: 'Hafta Sonu / Tatil' },
  ];

  const fetchFmRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/warning-rules?rule_type=fazla_mesai`, authH());
      setFmRules(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const openFmWarnModal = () => {
    setFmForm(emptyFmForm);
    setEditingFmId(null);
    fetchFmRules();
    fetchSubcontractors();
    setFmWarnOpen(true);
  };

  const toggleFmArr = (field, val) => {
    setFmForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const startEditFmRule = (r) => {
    setEditingFmId(r.id);
    setFmForm({
      title: r.title || '', overtime_types: r.overtime_types || [], firms: r.firms || [],
      period_type: r.period_type || 'month3', custom_days: r.custom_days ? String(r.custom_days) : '',
      threshold_days: r.threshold_days ? String(r.threshold_days) : '',
    });
  };

  const cancelFmEdit = () => { setEditingFmId(null); setFmForm(emptyFmForm); };

  const saveFmRule = async () => {
    if (!fmForm.title.trim()) { toast.error('Başlık zorunludur'); return; }
    if (!fmForm.overtime_types.length) { toast.error('En az bir mesai türü seçin'); return; }
    if (!fmForm.threshold_days || Number(fmForm.threshold_days) <= 0) { toast.error('Saat eşiği girin'); return; }
    if (fmForm.period_type === 'custom' && (!fmForm.custom_days || Number(fmForm.custom_days) <= 0)) { toast.error('Gün sayısı girin'); return; }
    setSavingFm(true);
    try {
      const body = {
        rule_type: 'fazla_mesai', title: fmForm.title.trim(), overtime_types: fmForm.overtime_types,
        firms: fmForm.firms, period_type: fmForm.period_type,
        custom_days: fmForm.period_type === 'custom' ? Number(fmForm.custom_days) : 0,
        threshold_days: Number(fmForm.threshold_days),
      };
      if (editingFmId) { await axios.put(`${API}/hr/warning-rules/${editingFmId}`, body, authH()); toast.success('Kural güncellendi'); }
      else { await axios.post(`${API}/hr/warning-rules`, body, authH()); toast.success('Kural eklendi'); }
      setFmForm(emptyFmForm); setEditingFmId(null); fetchFmRules();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kural kaydedilemedi'); }
    finally { setSavingFm(false); }
  };

  const deleteFmRule = async (id) => {
    try { await axios.delete(`${API}/hr/warning-rules/${id}`, authH()); toast.success('Kural silindi'); fetchFmRules(); }
    catch (e) { toast.error('Silinemedi'); }
  };

  // ===== Mesai (Devam) Uyarı Kuralları =====
  const MESAI_OPTS = [
    { key: 'is_late', label: 'İşe Geç Geldi' },
    { key: 'present', label: 'İşe Geldi' },
    { key: 'half_day', label: 'Yarım Gün' },
    { key: 'absent', label: 'Gelmedi' },
    { key: 'paid_leave', label: 'Ücretli İzinli' },
    { key: 'unpaid_leave', label: 'Ücretsiz İzinli' },
    { key: 'sick', label: 'Raporlu' },
    { key: 'annual', label: 'Yıllık İzin' },
    { key: 'holiday', label: 'Resmi Tatil' },
    { key: 'weekend', label: 'Hafta Sonu' },
    { key: 'holiday_overtime', label: 'Tatil Mesaisi' },
  ];
  const mesaiLabel = (k) => (MESAI_OPTS.find((o) => o.key === k)?.label || k);
  const emptyMesaiForm = { title: '', attendance_statuses: [], firms: [], period_type: 'month3', custom_days: '', threshold_days: '' };
  const [mesaiWarnOpen, setMesaiWarnOpen] = useState(false);
  const [mesaiRules, setMesaiRules] = useState([]);
  const [mesaiForm, setMesaiForm] = useState(emptyMesaiForm);
  const [savingMesai, setSavingMesai] = useState(false);
  const [editingMesaiId, setEditingMesaiId] = useState(null);

  const fetchMesaiRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/warning-rules?rule_type=mesai`, authH());
      setMesaiRules(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const openMesaiWarnModal = () => {
    setMesaiForm(emptyMesaiForm); setEditingMesaiId(null);
    fetchMesaiRules(); fetchSubcontractors(); setMesaiWarnOpen(true);
  };

  const toggleMesaiArr = (field, val) => {
    setMesaiForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const startEditMesaiRule = (r) => {
    setEditingMesaiId(r.id);
    setMesaiForm({
      title: r.title || '', attendance_statuses: r.attendance_statuses || [], firms: r.firms || [],
      period_type: r.period_type || 'month3', custom_days: r.custom_days ? String(r.custom_days) : '',
      threshold_days: r.threshold_days ? String(r.threshold_days) : '',
    });
  };

  const cancelMesaiEdit = () => { setEditingMesaiId(null); setMesaiForm(emptyMesaiForm); };

  const saveMesaiRule = async () => {
    if (!mesaiForm.title.trim()) { toast.error('Başlık zorunludur'); return; }
    if (!mesaiForm.attendance_statuses.length) { toast.error('En az bir mesai başlığı seçin'); return; }
    if (!mesaiForm.threshold_days || Number(mesaiForm.threshold_days) <= 0) { toast.error('Gün eşiği girin'); return; }
    if (mesaiForm.period_type === 'custom' && (!mesaiForm.custom_days || Number(mesaiForm.custom_days) <= 0)) { toast.error('Gün sayısı girin'); return; }
    setSavingMesai(true);
    try {
      const body = {
        rule_type: 'mesai', title: mesaiForm.title.trim(), attendance_statuses: mesaiForm.attendance_statuses,
        firms: mesaiForm.firms, period_type: mesaiForm.period_type,
        custom_days: mesaiForm.period_type === 'custom' ? Number(mesaiForm.custom_days) : 0,
        threshold_days: Number(mesaiForm.threshold_days),
      };
      if (editingMesaiId) { await axios.put(`${API}/hr/warning-rules/${editingMesaiId}`, body, authH()); toast.success('Kural güncellendi'); }
      else { await axios.post(`${API}/hr/warning-rules`, body, authH()); toast.success('Kural eklendi'); }
      setMesaiForm(emptyMesaiForm); setEditingMesaiId(null); fetchMesaiRules();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kural kaydedilemedi'); }
    finally { setSavingMesai(false); }
  };

  const deleteMesaiRule = async (id) => {
    try { await axios.delete(`${API}/hr/warning-rules/${id}`, authH()); toast.success('Kural silindi'); fetchMesaiRules(); }
    catch (e) { toast.error('Silinemedi'); }
  };

  // ===== Belge Uyarı Kuralları =====
  const DOC_OPTS = [
    { key: 'kimlik', label: 'Kimlik' },
    { key: 'ehliyet', label: 'Ehliyet' },
    { key: 'ikametgah', label: 'İkametgah' },
    { key: 'sabika', label: 'Sabıka Kaydı' },
    { key: 'diploma', label: 'Diploma' },
    { key: 'oda_kaydi', label: 'Oda Kaydı' },
    { key: 'saglik_raporu', label: 'Sağlık Raporu' },
    { key: 'cv', label: 'CV' },
    { key: 'ise_giris_bildirgesi', label: 'İşe Giriş Bildirgesi' },
    { key: 'aile_durum_bildirimi', label: 'Aile Durum Bildirimi' },
    { key: 'is_sozlesmesi', label: 'İş Sözleşmesi' },
    { key: 'kvkk', label: 'KVKK Belgesi' },
    { key: 'aydinlatma_metni', label: 'Çalışan Aydınlatma Metni' },
    { key: 'gorev_tanim', label: 'Görev Tanım Belgesi' },
    { key: 'isg_egitim', label: 'İSG Eğitim Belgeleri' },
  ];
  const docLabel = (k) => (DOC_OPTS.find((o) => o.key === k)?.label || k);
  const emptyBelgeForm = { title: '', document_types: [], custom_document_names: [], employee_ids: [], firms: [], threshold_days: '' };
  const [belgeWarnOpen, setBelgeWarnOpen] = useState(false);
  const [belgeRules, setBelgeRules] = useState([]);
  const [belgeForm, setBelgeForm] = useState(emptyBelgeForm);
  const [savingBelge, setSavingBelge] = useState(false);
  const [editingBelgeId, setEditingBelgeId] = useState(null);
  const [belgeCustomInput, setBelgeCustomInput] = useState('');
  const [belgeEmployees, setBelgeEmployees] = useState([]);
  const [belgeEmpSearch, setBelgeEmpSearch] = useState('');

  const fetchBelgeEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/employees`, authH());
      const list = (res.data?.employees || res.data || []).filter((e) => e.employment_status !== 'terminated' && !e.termination_date);
      setBelgeEmployees(list.map((e) => ({ id: e.id, name: e.name, branch_name: e.branch_name || '' })));
    } catch (e) { /* ignore */ }
  }, []);

  const toggleBelgeEmployee = (id) => {
    setBelgeForm((p) => {
      const arr = p.employee_ids || [];
      return { ...p, employee_ids: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] };
    });
  };

  const addCustomBelge = () => {
    const name = belgeCustomInput.trim();
    if (!name) return;
    setBelgeForm((p) => {
      const exists = (p.custom_document_names || []).some((x) => x.toLowerCase() === name.toLowerCase());
      if (exists) return p;
      return { ...p, custom_document_names: [...(p.custom_document_names || []), name] };
    });
    setBelgeCustomInput('');
  };

  const removeCustomBelge = (name) => {
    setBelgeForm((p) => ({ ...p, custom_document_names: (p.custom_document_names || []).filter((x) => x !== name) }));
  };

  const fetchBelgeRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/warning-rules?rule_type=belge`, authH());
      setBelgeRules(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const openBelgeWarnModal = () => {
    setBelgeForm(emptyBelgeForm); setEditingBelgeId(null); setBelgeCustomInput(''); setBelgeEmpSearch('');
    fetchBelgeRules(); fetchSubcontractors(); fetchBelgeEmployees(); setBelgeWarnOpen(true);
  };

  const toggleBelgeArr = (field, val) => {
    setBelgeForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const startEditBelgeRule = (r) => {
    setEditingBelgeId(r.id);
    setBelgeCustomInput(''); setBelgeEmpSearch('');
    setBelgeForm({
      title: r.title || '', document_types: r.document_types || [], custom_document_names: r.custom_document_names || [],
      employee_ids: r.employee_ids || [], firms: r.firms || [],
      threshold_days: (r.threshold_days || r.threshold_days === 0) ? String(r.threshold_days) : '',
    });
  };

  const cancelBelgeEdit = () => { setEditingBelgeId(null); setBelgeForm(emptyBelgeForm); setBelgeCustomInput(''); };

  const saveBelgeRule = async () => {
    if (!belgeForm.title.trim()) { toast.error('Başlık zorunludur'); return; }
    if (!belgeForm.document_types.length && !belgeForm.custom_document_names.length) { toast.error('En az bir belge türü seçin veya elle ekleyin'); return; }
    if (belgeForm.threshold_days === '' || Number(belgeForm.threshold_days) < 0) { toast.error('İşe giriş gün eşiği girin (0 veya üzeri)'); return; }
    setSavingBelge(true);
    try {
      const body = {
        rule_type: 'belge', title: belgeForm.title.trim(), document_types: belgeForm.document_types,
        custom_document_names: belgeForm.custom_document_names, employee_ids: belgeForm.employee_ids,
        firms: belgeForm.firms, period_type: 'custom', custom_days: 0,
        threshold_days: Number(belgeForm.threshold_days),
      };
      if (editingBelgeId) { await axios.put(`${API}/hr/warning-rules/${editingBelgeId}`, body, authH()); toast.success('Kural güncellendi'); }
      else { await axios.post(`${API}/hr/warning-rules`, body, authH()); toast.success('Kural eklendi'); }
      setBelgeForm(emptyBelgeForm); setEditingBelgeId(null); setBelgeCustomInput(''); fetchBelgeRules();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kural kaydedilemedi'); }
    finally { setSavingBelge(false); }
  };

  const deleteBelgeRule = async (id) => {
    try { await axios.delete(`${API}/hr/warning-rules/${id}`, authH()); toast.success('Kural silindi'); fetchBelgeRules(); }
    catch (e) { toast.error('Silinemedi'); }
  };

  // ===== Maaş Uyarı Kuralları =====
  const emptyMaasForm = { title: '', firms: [], threshold_days: '' };
  const [maasWarnOpen, setMaasWarnOpen] = useState(false);
  const [maasRules, setMaasRules] = useState([]);
  const [maasForm, setMaasForm] = useState(emptyMaasForm);
  const [savingMaas, setSavingMaas] = useState(false);
  const [editingMaasId, setEditingMaasId] = useState(null);

  const fetchMaasRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/warning-rules?rule_type=maas`, authH());
      setMaasRules(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const openMaasWarnModal = () => {
    setMaasForm(emptyMaasForm); setEditingMaasId(null);
    fetchMaasRules(); fetchSubcontractors(); setMaasWarnOpen(true);
  };

  const toggleMaasArr = (field, val) => {
    setMaasForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const startEditMaasRule = (r) => {
    setEditingMaasId(r.id);
    setMaasForm({
      title: r.title || '', firms: r.firms || [],
      threshold_days: (r.threshold_days || r.threshold_days === 0) ? String(r.threshold_days) : '',
    });
  };

  const cancelMaasEdit = () => { setEditingMaasId(null); setMaasForm(emptyMaasForm); };

  const saveMaasRule = async () => {
    if (!maasForm.title.trim()) { toast.error('Başlık zorunludur'); return; }
    if (!maasForm.threshold_days || Number(maasForm.threshold_days) <= 0) { toast.error('Ay eşiği girin (1 veya üzeri)'); return; }
    setSavingMaas(true);
    try {
      const body = {
        rule_type: 'maas', title: maasForm.title.trim(), firms: maasForm.firms,
        period_type: 'custom', custom_days: 0, threshold_days: Number(maasForm.threshold_days),
      };
      if (editingMaasId) { await axios.put(`${API}/hr/warning-rules/${editingMaasId}`, body, authH()); toast.success('Kural güncellendi'); }
      else { await axios.post(`${API}/hr/warning-rules`, body, authH()); toast.success('Kural eklendi'); }
      setMaasForm(emptyMaasForm); setEditingMaasId(null); fetchMaasRules();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kural kaydedilemedi'); }
    finally { setSavingMaas(false); }
  };

  const deleteMaasRule = async (id) => {
    try { await axios.delete(`${API}/hr/warning-rules/${id}`, authH()); toast.success('Kural silindi'); fetchMaasRules(); }
    catch (e) { toast.error('Silinemedi'); }
  };

  // ===== Avans Uyarı Kuralları =====
  const emptyAvForm = { title: '', firms: [], period_type: 'month3', custom_days: '', threshold_days: '' };
  const [avWarnOpen, setAvWarnOpen] = useState(false);
  const [avRules, setAvRules] = useState([]);
  const [avForm, setAvForm] = useState(emptyAvForm);
  const [savingAv, setSavingAv] = useState(false);
  const [editingAvId, setEditingAvId] = useState(null);

  const fetchAvRules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hr/warning-rules?rule_type=avans`, authH());
      setAvRules(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const openAvWarnModal = () => {
    setAvForm(emptyAvForm); setEditingAvId(null);
    fetchAvRules(); fetchSubcontractors(); setAvWarnOpen(true);
  };

  const toggleAvArr = (field, val) => {
    setAvForm((p) => {
      const arr = p[field] || [];
      return { ...p, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const startEditAvRule = (r) => {
    setEditingAvId(r.id);
    setAvForm({
      title: r.title || '', firms: r.firms || [], period_type: r.period_type || 'month3',
      custom_days: r.custom_days ? String(r.custom_days) : '', threshold_days: r.threshold_days ? String(r.threshold_days) : '',
    });
  };

  const cancelAvEdit = () => { setEditingAvId(null); setAvForm(emptyAvForm); };

  const saveAvRule = async () => {
    if (!avForm.title.trim()) { toast.error('Başlık zorunludur'); return; }
    if (!avForm.threshold_days || Number(avForm.threshold_days) <= 0) { toast.error('Tutar eşiği girin'); return; }
    if (avForm.period_type === 'custom' && (!avForm.custom_days || Number(avForm.custom_days) <= 0)) { toast.error('Gün sayısı girin'); return; }
    setSavingAv(true);
    try {
      const body = {
        rule_type: 'avans', title: avForm.title.trim(), firms: avForm.firms, period_type: avForm.period_type,
        custom_days: avForm.period_type === 'custom' ? Number(avForm.custom_days) : 0,
        threshold_days: Number(avForm.threshold_days),
      };
      if (editingAvId) { await axios.put(`${API}/hr/warning-rules/${editingAvId}`, body, authH()); toast.success('Kural güncellendi'); }
      else { await axios.post(`${API}/hr/warning-rules`, body, authH()); toast.success('Kural eklendi'); }
      setAvForm(emptyAvForm); setEditingAvId(null); fetchAvRules();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Kural kaydedilemedi'); }
    finally { setSavingAv(false); }
  };

  const deleteAvRule = async (id) => {
    try { await axios.delete(`${API}/hr/warning-rules/${id}`, authH()); toast.success('Kural silindi'); fetchAvRules(); }
    catch (e) { toast.error('Silinemedi'); }
  };

  const fetchAvans = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await axios.get(`${API}/hr/rules/avans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data?.data || {};
      setAvans({
        max_avans_yuzdesi: d.max_avans_yuzdesi ?? '',
        max_taksit_sayisi: d.max_taksit_sayisi ?? '',
        yillik_avans_adedi: d.yillik_avans_adedi ?? '',
        ilk_avans_gun: d.ilk_avans_gun ?? '',
        taksit_varken_avans: (d.taksit_varken_avans === 'evet' ? 'evet' : 'hayir'),
      });
    } catch (e) {
      console.error('Avans kuralları yüklenemedi:', e);
    }
  }, []);

  useEffect(() => {
    fetchAvans();
  }, [fetchAvans]);

  const handleSaveAvans = async () => {
    setSavingAvans(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const data = {
        max_avans_yuzdesi: Number(avans.max_avans_yuzdesi) || 0,
        max_taksit_sayisi: Number(avans.max_taksit_sayisi) || 0,
        yillik_avans_adedi: Number(avans.yillik_avans_adedi) || 0,
        ilk_avans_gun: Number(avans.ilk_avans_gun) || 0,
        taksit_varken_avans: avans.taksit_varken_avans === 'evet' ? 'evet' : 'hayir',
      };
      await axios.put(`${API}/hr/rules/avans`, { data }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Avans kuralları kaydedildi');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilirken hata oluştu');
    } finally {
      setSavingAvans(false);
    }
  };

  // ---- Yıllık İzin Kuralları ----
  const YEARS = Array.from({ length: 15 }, (_, i) => i + 1); // 1..15
  const [izin, setIzin] = useState({
    ilk_izin_gun: '',
    cumartesi_dahil: false,
    tatil_dahil: false,
    avans_izin: false,
  });
  const [yillikHaklar, setYillikHaklar] = useState({}); // { "1": 14, ... }
  const [savingIzin, setSavingIzin] = useState(false);
  const [showHaklarModal, setShowHaklarModal] = useState(false);
  const [draftHaklar, setDraftHaklar] = useState({});

  const fetchIzin = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      const res = await axios.get(`${API}/hr/rules/izin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data?.data || {};
      setIzin({
        ilk_izin_gun: d.ilk_izin_gun ?? '',
        cumartesi_dahil: !!d.cumartesi_dahil,
        tatil_dahil: !!d.tatil_dahil,
        avans_izin: !!d.avans_izin,
      });
      setYillikHaklar(d.yillik_haklar || {});
    } catch (e) {
      console.error('İzin kuralları yüklenemedi:', e);
    }
  }, []);

  useEffect(() => {
    fetchIzin();
  }, [fetchIzin]);

  const filledYearsCount = YEARS.filter((y) => {
    const v = yillikHaklar[String(y)];
    return v !== undefined && v !== '' && Number(v) > 0;
  }).length;

  const openHaklarModal = () => {
    // Mevcut değerleri taslağa kopyala
    const draft = {};
    YEARS.forEach((y) => { draft[String(y)] = yillikHaklar[String(y)] ?? ''; });
    setDraftHaklar(draft);
    setShowHaklarModal(true);
  };

  const saveHaklarModal = () => {
    const cleaned = {};
    YEARS.forEach((y) => {
      const v = draftHaklar[String(y)];
      if (v !== '' && v !== undefined && !isNaN(Number(v))) {
        cleaned[String(y)] = Number(v);
      }
    });
    setYillikHaklar(cleaned);
    setShowHaklarModal(false);
    toast.success('Yıllık izin hakları güncellendi. Kaydetmeyi unutmayın.');
  };

  const handleSaveIzin = async () => {
    setSavingIzin(true);
    try {
      const token = localStorage.getItem('fortis_token');
      const data = {
        ilk_izin_gun: Number(izin.ilk_izin_gun) || 0,
        yillik_haklar: yillikHaklar,
        cumartesi_dahil: !!izin.cumartesi_dahil,
        tatil_dahil: !!izin.tatil_dahil,
        avans_izin: !!izin.avans_izin,
      };
      await axios.put(`${API}/hr/rules/izin`, { data }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Yıllık izin kuralları kaydedildi');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kaydedilirken hata oluştu');
    } finally {
      setSavingIzin(false);
    }
  };

  const YesNoToggle = ({ value, onChange, testId }) => (
    <div className="flex items-center gap-2" data-testid={testId}>
      <button
        type="button"
        onClick={() => onChange(true)}
        className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${
          value ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
        }`}
        data-testid={`${testId}-evet`}
      >
        Evet
      </button>
      <button
        type="button"
        onClick={() => onChange(false)}
        className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${
          !value ? 'bg-gray-700 text-white border-gray-700' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
        }`}
        data-testid={`${testId}-hayir`}
      >
        Hayır
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="hr-rules-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 shadow-lg">
          <ScrollText className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kurallar</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">Şirket kural ve prosedürleri</p>
        </div>
      </div>

      {/* Avans Kuralları */}
      <RuleSection
        icon={Wallet}
        title="Avans Kuralları"
        accent="bg-gradient-to-r from-emerald-600 to-teal-600"
        testId="rule-section-avans"
      >
        <div className="space-y-4 max-w-2xl">
          {AVANS_FIELDS.map((f) => (
            <div key={f.key} className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4">
              <Label htmlFor={`avans-${f.key}`} className="text-sm font-medium text-gray-700">
                {f.label}
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id={`avans-${f.key}`}
                  type="number"
                  min="0"
                  value={avans[f.key]}
                  placeholder={f.placeholder}
                  onChange={(e) => setAvans((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  className="w-28"
                  data-testid={`avans-input-${f.key}`}
                />
                <span className="text-sm text-[hsl(var(--muted-foreground))] w-12">{f.suffix}</span>
              </div>
            </div>
          ))}
          {/* Taksit ödemesi devam ederken yeni avans talebi kuralı */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4 border-t pt-4 mt-2">
            <Label className="text-sm font-medium text-gray-700">
              Taksit ödemesi devam eden personel yeni avans talebi yapabilir mi?
            </Label>
            <div className="flex items-center gap-2" data-testid="avans-taksit-varken-toggle">
              <button
                type="button"
                onClick={() => setAvans((prev) => ({ ...prev, taksit_varken_avans: 'evet' }))}
                className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  avans.taksit_varken_avans === 'evet'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="avans-taksit-varken-evet"
              >
                Evet
              </button>
              <button
                type="button"
                onClick={() => setAvans((prev) => ({ ...prev, taksit_varken_avans: 'hayir' }))}
                className={`px-4 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  avans.taksit_varken_avans === 'hayir'
                    ? 'bg-red-600 text-white border-red-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="avans-taksit-varken-hayir"
              >
                Hayır
              </button>
            </div>
          </div>
          {avans.taksit_varken_avans === 'hayir' && (
            <p className="text-xs text-red-600 sm:col-span-2 -mt-2" data-testid="avans-taksit-varken-info">
              Maaşından hâlâ avans taksiti kesilen personel yeni avans talebinde bulunamaz; talep ekranında uyarı gösterilir.
            </p>
          )}
          <div className="pt-2">
            <Button
              onClick={handleSaveAvans}
              disabled={savingAvans}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="avans-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingAvans ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </RuleSection>

      {/* İzin Kuralları */}
      <RuleSection
        icon={CalendarDays}
        title="Yıllık İzin Kuralları"
        accent="bg-gradient-to-r from-indigo-600 to-blue-600"
        testId="rule-section-izin"
      >
        <div className="space-y-5 max-w-2xl">
          {/* 1. İlk izin gün */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4">
            <Label htmlFor="izin-ilk-gun" className="text-sm font-medium text-gray-700">
              Yıllık izin talebi işe başladıktan kaç gün sonra yapılabilir?
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="izin-ilk-gun"
                type="number"
                min="0"
                value={izin.ilk_izin_gun}
                placeholder="örn. 365"
                onChange={(e) => setIzin((prev) => ({ ...prev, ilk_izin_gun: e.target.value }))}
                className="w-28"
                data-testid="izin-input-ilk-gun"
              />
              <span className="text-sm text-[hsl(var(--muted-foreground))] w-12">gün</span>
            </div>
          </div>

          {/* 2. Yıllık izin hakları (1-15 yıl) modal */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4 border-t pt-4">
            <Label className="text-sm font-medium text-gray-700">
              Kıdem yılına göre izin gün hakları (1-15 yıl)
            </Label>
            <Button
              type="button"
              variant="outline"
              onClick={openHaklarModal}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="izin-haklar-modal-btn"
            >
              <ListChecks className="h-4 w-4 mr-2" />
              {filledYearsCount > 0 ? `Düzenle (${filledYearsCount}/15 yıl dolu)` : 'Yılları Gir'}
            </Button>
          </div>

          {/* Cumartesi günleri yıllık izinden düşülsün mü? (Pazar her zaman düşülmez) */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4 border-t pt-4">
            <Label className="text-sm font-medium text-gray-700">
              Cumartesi günleri yıllık izinden düşülsün mü?
              <span className="block text-xs text-gray-400 font-normal mt-0.5">Pazar günleri her zaman izinden düşülmez.</span>
            </Label>
            <YesNoToggle
              value={izin.cumartesi_dahil}
              onChange={(v) => setIzin((prev) => ({ ...prev, cumartesi_dahil: v }))}
              testId="izin-cumartesi-toggle"
            />
          </div>

          {/* 4. Tatil dahil */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4 border-t pt-4">
            <Label className="text-sm font-medium text-gray-700">
              Yıllık izinde resmi tatil günleri dahil edilsin mi?
            </Label>
            <YesNoToggle
              value={izin.tatil_dahil}
              onChange={(v) => setIzin((prev) => ({ ...prev, tatil_dahil: v }))}
              testId="izin-tatil-toggle"
            />
          </div>

          {/* Avans yıllık izin kullanılabilir mi? */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] items-center gap-2 sm:gap-4 border-t pt-4">
            <Label className="text-sm font-medium text-gray-700">
              Avans yıllık izin kullanılabilsin mi?
              <span className="block text-xs text-gray-400 font-normal mt-0.5">Evet ise; henüz hak etmemiş personel, izni avans olarak kullanabilir ve ileride hak edeceği yıllık izinden düşülür.</span>
            </Label>
            <YesNoToggle
              value={izin.avans_izin}
              onChange={(v) => setIzin((prev) => ({ ...prev, avans_izin: v }))}
              testId="izin-avans-toggle"
            />
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSaveIzin}
              disabled={savingIzin}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="izin-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {savingIzin ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </div>
        </div>
      </RuleSection>

      {/* Uyarı Kuralları */}
      <RuleSection
        icon={AlertTriangle}
        title="Uyarı Kuralları"
        accent="bg-gradient-to-r from-amber-500 to-orange-600"
        testId="rule-section-warnings"
      >
        <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
          Aşağıdaki uyarı türleri için kurallar tanımlayın. Oluşan uyarılar İK Paneli &gt; <b>Uyarılar</b> sayfasında listelenir.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {WARNING_RULE_CATEGORIES.map((c) => {
            const CIcon = c.icon;
            const isIzin = c.key === 'izin';
            const isFm = c.key === 'fazla_mesai';
            const isAv = c.key === 'avans';
            const isMesai = c.key === 'mesai';
            const isBelge = c.key === 'belge';
            const isMaas = c.key === 'maas';
            const clickable = isIzin || isFm || isAv || isMesai || isBelge || isMaas;
            const onClick = isIzin ? openIzinWarnModal : (isFm ? openFmWarnModal : (isAv ? openAvWarnModal : (isMesai ? openMesaiWarnModal : (isBelge ? openBelgeWarnModal : (isMaas ? openMaasWarnModal : undefined)))));
            return (
              <div
                key={c.key}
                data-testid={`warning-rule-${c.key}`}
                onClick={onClick}
                className={`flex items-start gap-3 p-4 rounded-xl border border-amber-200 bg-amber-50/50 transition-colors ${clickable ? 'cursor-pointer hover:bg-amber-100 hover:border-amber-300' : 'hover:bg-amber-50'}`}
              >
                <div className="p-2 rounded-lg bg-amber-100 shrink-0">
                  <CIcon className="h-5 w-5 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-gray-900">{c.label}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{c.desc}</div>
                  {clickable ? (
                    <div className="text-xs text-amber-700 mt-2 font-medium">Kuralları yönet →</div>
                  ) : (
                    <div className="text-xs text-amber-700 mt-2 font-medium">Kurallar tanımlanacak</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </RuleSection>

      {/* Yıllık İzin Hakları Modal (1-15 yıl) */}
      <Dialog open={showHaklarModal} onOpenChange={setShowHaklarModal}>
        <DialogContent className="max-w-md" data-testid="izin-haklar-modal">
          <DialogHeader>
            <DialogTitle>Kıdem Yılına Göre İzin Hakları</DialogTitle>
            <DialogDescription>
              Her kıdem yılı için çalışanın kaç gün yıllık izin hakkı olduğunu girin (1-15 yıl).
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[50vh] overflow-y-auto py-2">
            {YEARS.map((y) => (
              <div key={y} className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 w-16">{y}. Yıl</span>
                <Input
                  type="number"
                  min="0"
                  value={draftHaklar[String(y)] ?? ''}
                  placeholder="gün"
                  onChange={(e) => setDraftHaklar((prev) => ({ ...prev, [String(y)]: e.target.value }))}
                  className="w-20"
                  data-testid={`izin-hak-yil-${y}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHaklarModal(false)}>İptal</Button>
            <Button
              onClick={saveHaklarModal}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="izin-haklar-modal-save"
            >
              Tamam
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* İzin Uyarı Kuralları Modal */}
      <Dialog open={izinWarnOpen} onOpenChange={setIzinWarnOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="izin-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> İzin Uyarı Kuralları</DialogTitle>
            <DialogDescription>
              Şartı sağlayan çalışanlar otomatik olarak İK &gt; Uyarılar sayfasında listelenir. Birden fazla kural ekleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          {/* Mevcut kurallar */}
          {izinRules.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Tanımlı Kurallar</Label>
              {izinRules.map((r) => (
                <div key={r.id} data-testid={`izin-rule-item-${r.id}`} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${editingRuleId === r.id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {(r.leave_types || []).map((t) => t === 'paid_leave' ? 'Ücretli' : t === 'unpaid_leave' ? 'Ücretsiz' : t).join(', ')}
                      {' · '}
                      {r.period_type === 'month1' ? 'Son 1 Ay' : r.period_type === 'month3' ? 'Son 3 Ay' : r.period_type === 'year1' ? 'Son 1 Yıl' : `Son ${r.custom_days} Gün`}
                      {' · '}{r.threshold_days} günden fazla
                      {(r.firms && r.firms.length) ? ` · ${r.firms.length} firma` : ' · Tüm firmalar'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => startEditRule(r)} data-testid={`izin-rule-edit-${r.id}`}>Düzenle</Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteIzinRule(r.id)} data-testid={`izin-rule-delete-${r.id}`}>Sil</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Yeni kural formu */}
          <div className="space-y-4 border-t pt-4">
            <div className="font-medium text-sm">{editingRuleId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</div>
            <div>
              <Label>Başlık *</Label>
              <Input
                value={wForm.title}
                onChange={(e) => setWForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="örn. Son 3 ayda 3+ gün ücretsiz izin alanlar"
                data-testid="izin-warn-title"
              />
            </div>

            <div>
              <Label>İzin Türü (birden fazla seçilebilir)</Label>
              <div className="flex gap-2 mt-1">
                {LEAVE_OPTS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => toggleArr('leave_types', o.key)}
                    data-testid={`izin-warn-leavetype-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${wForm.leave_types.includes(o.key) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Firmalar (boş bırakılırsa tümü)</Label>
              <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                {firmOptions.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => toggleArr('firms', f.id)}
                    data-testid={`izin-warn-firm-${f.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${wForm.firms.includes(f.id) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Süre</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PERIOD_OPTS.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => setWForm((p) => ({ ...p, period_type: o.key }))}
                    data-testid={`izin-warn-period-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${wForm.period_type === o.key ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {wForm.period_type === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="number" min="1" className="w-28" value={wForm.custom_days} placeholder="gün" onChange={(e) => setWForm((p) => ({ ...p, custom_days: e.target.value }))} data-testid="izin-warn-custom-days" />
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">gün içinde</span>
                </div>
              )}
            </div>

            <div>
              <Label>İzin Eşiği</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="1" className="w-28" value={wForm.threshold_days} placeholder="örn. 3" onChange={(e) => setWForm((p) => ({ ...p, threshold_days: e.target.value }))} data-testid="izin-warn-threshold" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">günden fazla izin olunca uyarı çıksın</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIzinWarnOpen(false)}>Kapat</Button>
            {editingRuleId && (
              <Button variant="outline" onClick={cancelEdit} data-testid="izin-warn-cancel-edit">Vazgeç</Button>
            )}
            <Button onClick={saveIzinRule} disabled={savingWarn} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="izin-warn-save">
              <Save className="h-4 w-4 mr-2" />{savingWarn ? 'Kaydediliyor...' : (editingRuleId ? 'Güncelle' : 'Kural Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Maaş Uyarı Kuralları Modal */}
      <Dialog open={maasWarnOpen} onOpenChange={setMaasWarnOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="maas-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Maaş Uyarı Kuralları</DialogTitle>
            <DialogDescription>
              Maaşı (son maaş değişikliği) belirlediğiniz aydan daha uzun süredir değişmemiş çalışanlar otomatik olarak İK &gt; Uyarılar sayfasında listelenir. Birden fazla kural ekleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          {maasRules.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Tanımlı Kurallar</Label>
              {maasRules.map((r) => (
                <div key={r.id} data-testid={`maas-rule-item-${r.id}`} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${editingMaasId === r.id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {r.threshold_days} aydır maaşı değişmeyenler
                      {(r.firms && r.firms.length) ? ` · ${r.firms.length} firma` : ' · Tüm firmalar'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => startEditMaasRule(r)} data-testid={`maas-rule-edit-${r.id}`}>Düzenle</Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteMaasRule(r.id)} data-testid={`maas-rule-delete-${r.id}`}>Sil</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 border-t pt-4">
            <div className="font-medium text-sm">{editingMaasId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</div>
            <div>
              <Label>Başlık *</Label>
              <Input value={maasForm.title} onChange={(e) => setMaasForm((p) => ({ ...p, title: e.target.value }))} placeholder="örn. 12 aydır maaşı değişmeyenler" data-testid="maas-warn-title" />
            </div>

            <div>
              <Label>Firmalar (boş bırakılırsa tümü)</Label>
              <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                {firmOptions.map((f) => (
                  <button key={f.id} type="button" onClick={() => toggleMaasArr('firms', f.id)} data-testid={`maas-warn-firm-${f.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${maasForm.firms.includes(f.id) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Süre Eşiği (ay)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="1" className="w-28" value={maasForm.threshold_days} placeholder="örn. 12" onChange={(e) => setMaasForm((p) => ({ ...p, threshold_days: e.target.value }))} data-testid="maas-warn-threshold" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">aydan uzun süredir maaşı değişmeyenler için uyarı çıksın</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMaasWarnOpen(false)}>Kapat</Button>
            {editingMaasId && <Button variant="outline" onClick={cancelMaasEdit} data-testid="maas-warn-cancel-edit">Vazgeç</Button>}
            <Button onClick={saveMaasRule} disabled={savingMaas} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="maas-warn-save">
              <Save className="h-4 w-4 mr-2" />{savingMaas ? 'Kaydediliyor...' : (editingMaasId ? 'Güncelle' : 'Kural Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Belge Uyarı Kuralları Modal */}
      <Dialog open={belgeWarnOpen} onOpenChange={setBelgeWarnOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="belge-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Belge Uyarı Kuralları</DialogTitle>
            <DialogDescription>
              İşe girişinden belirlediğiniz günden fazla geçmiş olup seçili zorunlu belgelerden eksiği olan çalışanlar otomatik olarak İK &gt; Uyarılar sayfasında listelenir. Birden fazla kural ekleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          {belgeRules.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Tanımlı Kurallar</Label>
              {belgeRules.map((r) => (
                <div key={r.id} data-testid={`belge-rule-item-${r.id}`} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${editingBelgeId === r.id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {[...(r.document_types || []).map(docLabel), ...(r.custom_document_names || [])].join(', ')}
                      {' · '}İşe girişten {r.threshold_days} gün sonra eksikse
                      {(r.employee_ids && r.employee_ids.length) ? ` · ${r.employee_ids.length} kişi` : (r.firms && r.firms.length) ? ` · ${r.firms.length} firma` : ' · Tüm firmalar'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => startEditBelgeRule(r)} data-testid={`belge-rule-edit-${r.id}`}>Düzenle</Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteBelgeRule(r.id)} data-testid={`belge-rule-delete-${r.id}`}>Sil</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 border-t pt-4">
            <div className="font-medium text-sm">{editingBelgeId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</div>
            <div>
              <Label>Başlık *</Label>
              <Input value={belgeForm.title} onChange={(e) => setBelgeForm((p) => ({ ...p, title: e.target.value }))} placeholder="örn. İşe girişten 15 gün sonra sözleşmesi eksik olanlar" data-testid="belge-warn-title" />
            </div>

            <div>
              <Label>Zorunlu Belgeler (birden fazla seçilebilir) *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DOC_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => toggleBelgeArr('document_types', o.key)} data-testid={`belge-warn-doc-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${belgeForm.document_types.includes(o.key) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Elle Belge Ekle (listede olmayanlar için)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  value={belgeCustomInput}
                  onChange={(e) => setBelgeCustomInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomBelge(); } }}
                  placeholder="örn. Vesikalık Fotoğraf"
                  data-testid="belge-warn-custom-input"
                />
                <Button type="button" variant="outline" onClick={addCustomBelge} data-testid="belge-warn-custom-add">Ekle</Button>
              </div>
              {belgeForm.custom_document_names.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {belgeForm.custom_document_names.map((nm) => (
                    <span key={nm} data-testid={`belge-warn-custom-chip-${nm}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border bg-emerald-600 text-white border-emerald-600">
                      {nm}
                      <button type="button" onClick={() => removeCustomBelge(nm)} className="ml-1 hover:text-emerald-200" data-testid={`belge-warn-custom-remove-${nm}`}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Çalışan evrakına elle eklenen belgelerle isim olarak eşleşir.</p>
            </div>

            <div>
              <Label>Firmalar (boş bırakılırsa tümü)</Label>
              <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                {firmOptions.map((f) => (
                  <button key={f.id} type="button" onClick={() => toggleBelgeArr('firms', f.id)} data-testid={`belge-warn-firm-${f.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${belgeForm.firms.includes(f.id) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Kişi Bazlı Seç (opsiyonel)</Label>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">Belirli çalışan(lar) seçerseniz kural yalnızca onlara uygulanır (firma filtresi yok sayılır).</p>
              <Input
                value={belgeEmpSearch}
                onChange={(e) => setBelgeEmpSearch(e.target.value)}
                placeholder="Çalışan ara (isim)..."
                data-testid="belge-warn-emp-search"
              />
              {belgeForm.employee_ids.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {belgeForm.employee_ids.map((eid) => {
                    const emp = belgeEmployees.find((x) => x.id === eid);
                    return (
                      <span key={eid} data-testid={`belge-warn-emp-chip-${eid}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm border bg-blue-600 text-white border-blue-600">
                        {emp ? emp.name : eid}
                        <button type="button" onClick={() => toggleBelgeEmployee(eid)} className="ml-1 hover:text-blue-200" data-testid={`belge-warn-emp-remove-${eid}`}>×</button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="flex flex-col gap-1 mt-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {belgeEmployees
                  .filter((e) => !belgeEmpSearch.trim() || (e.name || '').toLowerCase().includes(belgeEmpSearch.trim().toLowerCase()))
                  .slice(0, 50)
                  .map((e) => (
                    <button key={e.id} type="button" onClick={() => toggleBelgeEmployee(e.id)} data-testid={`belge-warn-emp-${e.id}`}
                      className={`flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-sm text-left transition-colors ${belgeForm.employee_ids.includes(e.id) ? 'bg-blue-50 text-blue-700 font-medium' : 'hover:bg-gray-50 text-gray-700'}`}>
                      <span className="truncate">{e.name}{e.branch_name ? ` · ${e.branch_name}` : ''}</span>
                      {belgeForm.employee_ids.includes(e.id) && <span className="text-blue-600 shrink-0">✓</span>}
                    </button>
                  ))}
                {belgeEmployees.length === 0 && <span className="text-xs text-[hsl(var(--muted-foreground))] px-2 py-1">Çalışan listesi yükleniyor...</span>}
              </div>
            </div>


            <div>
              <Label>İşe Giriş Sonrası Gün (kıdem eşiği)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="0" className="w-28" value={belgeForm.threshold_days} placeholder="örn. 15" onChange={(e) => setBelgeForm((p) => ({ ...p, threshold_days: e.target.value }))} data-testid="belge-warn-threshold" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">günden fazla süredir işe girip belgesi eksik olunca uyar (0 = hemen)</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBelgeWarnOpen(false)}>Kapat</Button>
            {editingBelgeId && <Button variant="outline" onClick={cancelBelgeEdit} data-testid="belge-warn-cancel-edit">Vazgeç</Button>}
            <Button onClick={saveBelgeRule} disabled={savingBelge} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="belge-warn-save">
              <Save className="h-4 w-4 mr-2" />{savingBelge ? 'Kaydediliyor...' : (editingBelgeId ? 'Güncelle' : 'Kural Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mesai (Devam) Uyarı Kuralları Modal */}
      <Dialog open={mesaiWarnOpen} onOpenChange={setMesaiWarnOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="mesai-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Mesai Uyarı Kuralları</DialogTitle>
            <DialogDescription>
              Seçtiğiniz mesai başlıklarından periyot içinde eşikten fazla gün biriken çalışanlar otomatik olarak İK &gt; Uyarılar sayfasında listelenir. Birden fazla kural ekleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          {mesaiRules.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Tanımlı Kurallar</Label>
              {mesaiRules.map((r) => (
                <div key={r.id} data-testid={`mesai-rule-item-${r.id}`} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${editingMesaiId === r.id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {(r.attendance_statuses || []).map(mesaiLabel).join(', ')}
                      {' · '}
                      {r.period_type === 'month1' ? 'Son 1 Ay' : r.period_type === 'month3' ? 'Son 3 Ay' : r.period_type === 'year1' ? 'Son 1 Yıl' : `Son ${r.custom_days} Gün`}
                      {' · '}{r.threshold_days} günden fazla
                      {(r.firms && r.firms.length) ? ` · ${r.firms.length} firma` : ' · Tüm firmalar'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => startEditMesaiRule(r)} data-testid={`mesai-rule-edit-${r.id}`}>Düzenle</Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteMesaiRule(r.id)} data-testid={`mesai-rule-delete-${r.id}`}>Sil</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 border-t pt-4">
            <div className="font-medium text-sm">{editingMesaiId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</div>
            <div>
              <Label>Başlık *</Label>
              <Input value={mesaiForm.title} onChange={(e) => setMesaiForm((p) => ({ ...p, title: e.target.value }))} placeholder="örn. Son 1 ayda 3+ kez işe geç gelenler" data-testid="mesai-warn-title" />
            </div>

            <div>
              <Label>Mesai Başlıkları (birden fazla seçilebilir) *</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {MESAI_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => toggleMesaiArr('attendance_statuses', o.key)} data-testid={`mesai-warn-status-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mesaiForm.attendance_statuses.includes(o.key) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Firmalar (boş bırakılırsa tümü)</Label>
              <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                {firmOptions.map((f) => (
                  <button key={f.id} type="button" onClick={() => toggleMesaiArr('firms', f.id)} data-testid={`mesai-warn-firm-${f.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mesaiForm.firms.includes(f.id) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Süre</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PERIOD_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => setMesaiForm((p) => ({ ...p, period_type: o.key }))} data-testid={`mesai-warn-period-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${mesaiForm.period_type === o.key ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {mesaiForm.period_type === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="number" min="1" className="w-28" value={mesaiForm.custom_days} placeholder="gün" onChange={(e) => setMesaiForm((p) => ({ ...p, custom_days: e.target.value }))} data-testid="mesai-warn-custom-days" />
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">gün içinde</span>
                </div>
              )}
            </div>

            <div>
              <Label>Gün Eşiği</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="1" className="w-28" value={mesaiForm.threshold_days} placeholder="örn. 3" onChange={(e) => setMesaiForm((p) => ({ ...p, threshold_days: e.target.value }))} data-testid="mesai-warn-threshold" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">günden fazla olunca uyarı çıksın</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setMesaiWarnOpen(false)}>Kapat</Button>
            {editingMesaiId && <Button variant="outline" onClick={cancelMesaiEdit} data-testid="mesai-warn-cancel-edit">Vazgeç</Button>}
            <Button onClick={saveMesaiRule} disabled={savingMesai} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="mesai-warn-save">
              <Save className="h-4 w-4 mr-2" />{savingMesai ? 'Kaydediliyor...' : (editingMesaiId ? 'Güncelle' : 'Kural Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fazla Mesai Uyarı Kuralları Modal */}
      <Dialog open={fmWarnOpen} onOpenChange={setFmWarnOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="fm-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Fazla Mesai Uyarı Kuralları</DialogTitle>
            <DialogDescription>
              Şartı sağlayan çalışanlar otomatik olarak İK &gt; Uyarılar sayfasında listelenir. Birden fazla kural ekleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          {fmRules.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Tanımlı Kurallar</Label>
              {fmRules.map((r) => (
                <div key={r.id} data-testid={`fm-rule-item-${r.id}`} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${editingFmId === r.id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {(r.overtime_types || []).map((t) => t === 'weekday' ? 'Gün İçi' : t === 'holiday' ? 'Tatil' : t).join(', ')}
                      {' · '}
                      {r.period_type === 'month1' ? 'Son 1 Ay' : r.period_type === 'month3' ? 'Son 3 Ay' : r.period_type === 'year1' ? 'Son 1 Yıl' : `Son ${r.custom_days} Gün`}
                      {' · '}{r.threshold_days} saatten fazla
                      {(r.firms && r.firms.length) ? ` · ${r.firms.length} firma` : ' · Tüm firmalar'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => startEditFmRule(r)} data-testid={`fm-rule-edit-${r.id}`}>Düzenle</Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteFmRule(r.id)} data-testid={`fm-rule-delete-${r.id}`}>Sil</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 border-t pt-4">
            <div className="font-medium text-sm">{editingFmId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</div>
            <div>
              <Label>Başlık *</Label>
              <Input value={fmForm.title} onChange={(e) => setFmForm((p) => ({ ...p, title: e.target.value }))} placeholder="örn. Son 1 ayda 40+ saat fazla mesai yapanlar" data-testid="fm-warn-title" />
            </div>

            <div>
              <Label>Mesai Türü (birden fazla seçilebilir)</Label>
              <div className="flex gap-2 mt-1">
                {OT_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => toggleFmArr('overtime_types', o.key)} data-testid={`fm-warn-ottype-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${fmForm.overtime_types.includes(o.key) ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Firmalar (boş bırakılırsa tümü)</Label>
              <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                {firmOptions.map((f) => (
                  <button key={f.id} type="button" onClick={() => toggleFmArr('firms', f.id)} data-testid={`fm-warn-firm-${f.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${fmForm.firms.includes(f.id) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Süre</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PERIOD_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => setFmForm((p) => ({ ...p, period_type: o.key }))} data-testid={`fm-warn-period-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${fmForm.period_type === o.key ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {fmForm.period_type === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="number" min="1" className="w-28" value={fmForm.custom_days} placeholder="gün" onChange={(e) => setFmForm((p) => ({ ...p, custom_days: e.target.value }))} data-testid="fm-warn-custom-days" />
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">gün içinde</span>
                </div>
              )}
            </div>

            <div>
              <Label>Saat Eşiği</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="1" step="0.5" className="w-28" value={fmForm.threshold_days} placeholder="örn. 40" onChange={(e) => setFmForm((p) => ({ ...p, threshold_days: e.target.value }))} data-testid="fm-warn-threshold" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">saatten fazla fazla mesai olunca uyarı çıksın</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFmWarnOpen(false)}>Kapat</Button>
            {editingFmId && <Button variant="outline" onClick={cancelFmEdit} data-testid="fm-warn-cancel-edit">Vazgeç</Button>}
            <Button onClick={saveFmRule} disabled={savingFm} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="fm-warn-save">
              <Save className="h-4 w-4 mr-2" />{savingFm ? 'Kaydediliyor...' : (editingFmId ? 'Güncelle' : 'Kural Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Avans Uyarı Kuralları Modal */}
      <Dialog open={avWarnOpen} onOpenChange={setAvWarnOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="av-warning-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Avans Uyarı Kuralları</DialogTitle>
            <DialogDescription>
              Şartı sağlayan çalışanlar otomatik olarak İK &gt; Uyarılar sayfasında listelenir. Birden fazla kural ekleyebilirsiniz.
            </DialogDescription>
          </DialogHeader>

          {avRules.length > 0 && (
            <div className="space-y-2 mb-4">
              <Label className="text-xs text-[hsl(var(--muted-foreground))]">Tanımlı Kurallar</Label>
              {avRules.map((r) => (
                <div key={r.id} data-testid={`av-rule-item-${r.id}`} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${editingAvId === r.id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50'}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{r.title}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      {r.period_type === 'month1' ? 'Son 1 Ay' : r.period_type === 'month3' ? 'Son 3 Ay' : r.period_type === 'year1' ? 'Son 1 Yıl' : `Son ${r.custom_days} Gün`}
                      {' · '}{r.threshold_days} ₺ üzeri
                      {(r.firms && r.firms.length) ? ` · ${r.firms.length} firma` : ' · Tüm firmalar'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50" onClick={() => startEditAvRule(r)} data-testid={`av-rule-edit-${r.id}`}>Düzenle</Button>
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => deleteAvRule(r.id)} data-testid={`av-rule-delete-${r.id}`}>Sil</Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-4 border-t pt-4">
            <div className="font-medium text-sm">{editingAvId ? 'Kuralı Düzenle' : 'Yeni Kural Ekle'}</div>
            <div>
              <Label>Başlık *</Label>
              <Input value={avForm.title} onChange={(e) => setAvForm((p) => ({ ...p, title: e.target.value }))} placeholder="örn. Son 3 ayda 10.000₺ üzeri avans alanlar" data-testid="av-warn-title" />
            </div>

            <div>
              <Label>Firmalar (boş bırakılırsa tümü)</Label>
              <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto">
                {firmOptions.map((f) => (
                  <button key={f.id} type="button" onClick={() => toggleAvArr('firms', f.id)} data-testid={`av-warn-firm-${f.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${avForm.firms.includes(f.id) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {f.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Süre</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {PERIOD_OPTS.map((o) => (
                  <button key={o.key} type="button" onClick={() => setAvForm((p) => ({ ...p, period_type: o.key }))} data-testid={`av-warn-period-${o.key}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${avForm.period_type === o.key ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
              {avForm.period_type === 'custom' && (
                <div className="flex items-center gap-2 mt-2">
                  <Input type="number" min="1" className="w-28" value={avForm.custom_days} placeholder="gün" onChange={(e) => setAvForm((p) => ({ ...p, custom_days: e.target.value }))} data-testid="av-warn-custom-days" />
                  <span className="text-sm text-[hsl(var(--muted-foreground))]">gün içinde</span>
                </div>
              )}
            </div>

            <div>
              <Label>Tutar Eşiği (₺)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="1" step="100" className="w-36" value={avForm.threshold_days} placeholder="örn. 10000" onChange={(e) => setAvForm((p) => ({ ...p, threshold_days: e.target.value }))} data-testid="av-warn-threshold" />
                <span className="text-sm text-[hsl(var(--muted-foreground))]">₺ üzeri avans olunca uyarı çıksın</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAvWarnOpen(false)}>Kapat</Button>
            {editingAvId && <Button variant="outline" onClick={cancelAvEdit} data-testid="av-warn-cancel-edit">Vazgeç</Button>}
            <Button onClick={saveAvRule} disabled={savingAv} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="av-warn-save">
              <Save className="h-4 w-4 mr-2" />{savingAv ? 'Kaydediliyor...' : (editingAvId ? 'Güncelle' : 'Kural Ekle')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRRules;
